import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ArrowLeft, Check, Eye, EyeOff } from 'lucide-react';
import { env } from '@config/env';
import { interpolateCopy, pageCopy } from '@shared/lib/pageCopy';
import { Button } from '@shared/ui/primitives/Button';
import { Field } from '@shared/ui/primitives/Field';
import { Banner } from '@shared/ui/feedback/Banner';
import { Skeleton } from '@shared/ui/feedback/Skeleton';
import { useDebounce } from '@shared/hooks/useDebounce';
import { useModuleStore } from '@shared/stores/moduleStore';
import { resetToHubContext } from '@shared/api/domainApi';
import { getEffectiveRootTenantId } from '@shared/lib/ssoRootScope';
import { buildE164 } from '@shared/lib/phone';
import { focusNextOnEnter, applyEnterKeyHint } from '@shared/lib/formNav';
import { CountryCodePicker, findCountry } from '@shared/ui/primitives/CountryCodePicker';
import { toApiError } from '@shared/api/errors';
import {
  login,
  fetchMemberTypes,
  fetchDomainTerms,
  checkSignupAvailability,
  requestSignupOtp,
  verifySignupOtp,
  verifySignupAndCreate,
  type SignupMemberData,
} from './api';

type Phase = 'form' | 'otp';
type Method = 'email' | 'mobile';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Create-account screen. Mirrors the Angular member-portal signup:
 *   1. Core auth mode → the community is picked on the welcome screen and
 *      arrives here as the `?tenant=` query param, already resolved. A
 *      bare `/signup` on an SSO build is bounced back to the picker.
 *   2. The form honours the tenant's `authConfig`, member-type selection,
 *      live availability checks, and the tenant's terms documents.
 *   3. Ownership is confirmed by OTP before the account is created.
 * A per-domain build (no root scope) goes straight to the form.
 */
export function Signup(): JSX.Element {
  const authCopy = pageCopy().auth;
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const rootTenantId = getEffectiveRootTenantId(env.rootTenantId);
  const tenantParam = params.get('tenant');
  const authConfig = useModuleStore((s) => s.config?.authConfig);
  const currentDomainConfigId = useModuleStore((s) => s.config?.domainConfigId);

  // SSO builds require a community; per-domain builds are always "chosen".
  const communityChosen = !rootTenantId || !!tenantParam;

  const [phase, setPhase] = useState<Phase>('form');
  const [error, setError] = useState<string | null>(null);

  // ── Form fields ───────────────────────────────────────────────
  const [memberTypeId, setMemberTypeId] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [countryCode, setCountryCode] = useState('ZA');
  const [localMobile, setLocalMobile] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [verifyMethod, setVerifyMethod] = useState<Method>('email');
  const [agreed, setAgreed] = useState(false);
  const [acceptedTermIds, setAcceptedTermIds] = useState<Record<string, boolean>>({});

  // ── OTP phase ─────────────────────────────────────────────────
  const [queue, setQueue] = useState<Method[]>([]);
  const [qIndex, setQIndex] = useState(0);
  const [otpCode, setOtpCode] = useState('');

  const completedRef = useRef(false);
  // The hub/base domain-config id — used as the fallback source of terms
  // documents. The welcome screen captures it before resolving the chosen
  // community and hands it over via navigation state.
  const navState = location.state as { baseDomainConfigId?: string } | null;
  const baseDomainConfigIdRef = useRef<string | undefined>(navState?.baseDomainConfigId);
  useEffect(() => {
    if (!baseDomainConfigIdRef.current) {
      baseDomainConfigIdRef.current = useModuleStore.getState().config?.domainConfigId ?? undefined;
    }
    return () => {
      // Leaving signup incomplete reverts branding + domain context to the hub.
      if (!completedRef.current) void resetToHubContext();
    };
  }, []);

  const allowedIdentifiers = authConfig?.allowed_identifiers ?? 'email';
  const verificationMode = authConfig?.signup_verification_mode ?? 'email_or_mobile';

  const showEmail = allowedIdentifiers === 'email' || allowedIdentifiers === 'both';
  const showMobile =
    allowedIdentifiers === 'mobile' ||
    allowedIdentifiers === 'both' ||
    verificationMode === 'mobile' ||
    verificationMode === 'both';
  const showMethodToggle = verificationMode === 'email_or_mobile' && showEmail && showMobile;

  const emailRequired =
    showEmail &&
    (allowedIdentifiers === 'email' ||
      verificationMode === 'email' ||
      verificationMode === 'both' ||
      (verificationMode === 'email_or_mobile' && verifyMethod === 'email'));
  const mobileRequired =
    showMobile &&
    (allowedIdentifiers === 'mobile' ||
      verificationMode === 'mobile' ||
      verificationMode === 'both' ||
      (verificationMode === 'email_or_mobile' && verifyMethod === 'mobile'));

  const dialCode = findCountry(countryCode).dialCode;
  const mobileE164 = useMemo(() => buildE164(dialCode, localMobile), [dialCode, localMobile]);
  const emailOk = EMAIL_RE.test(email.trim());

  const memberTypesQuery = useQuery({
    queryKey: ['member-types', currentDomainConfigId],
    queryFn: fetchMemberTypes,
    enabled: communityChosen,
    staleTime: 5 * 60_000,
  });
  const memberTypes = memberTypesQuery.data ?? [];

  // ── Terms — the tenant's documents, falling back to the base tenant ──
  const termsQuery = useQuery({
    queryKey: ['domain-terms', currentDomainConfigId, baseDomainConfigIdRef.current],
    queryFn: async () => {
      let terms = currentDomainConfigId
        ? await fetchDomainTerms(currentDomainConfigId).catch(() => [])
        : [];
      const base = baseDomainConfigIdRef.current;
      if (terms.length === 0 && base && base !== currentDomainConfigId) {
        terms = await fetchDomainTerms(base).catch(() => []);
      }
      return terms;
    },
    enabled: communityChosen && !!currentDomainConfigId,
    staleTime: 5 * 60_000,
  });
  const terms = termsQuery.data ?? [];
  const termsOk =
    terms.length > 0 ? terms.every((t) => acceptedTermIds[t.terms_id]) : agreed;

  // ── Availability checks ───────────────────────────────────────
  const debouncedEmail = useDebounce(email.trim().toLowerCase(), 600);
  const debouncedMobile = useDebounce(mobileE164, 600);

  const emailAvail = useQuery({
    queryKey: ['signup-avail-email', debouncedEmail],
    queryFn: () => checkSignupAvailability({ email: debouncedEmail }),
    enabled: communityChosen && showEmail && EMAIL_RE.test(debouncedEmail),
  });
  const mobileAvail = useQuery({
    queryKey: ['signup-avail-mobile', debouncedMobile],
    queryFn: () => checkSignupAvailability({ mobile: debouncedMobile }),
    enabled: communityChosen && showMobile && debouncedMobile.length >= 8,
  });

  const emailTaken = emailAvail.data?.email?.available === false;
  const mobileTaken = mobileAvail.data?.mobile?.available === false;

  const buildMemberData = (): SignupMemberData => {
    const ids = terms.filter((t) => acceptedTermIds[t.terms_id]).map((t) => t.terms_id);
    return {
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      phone: mobileE164 || undefined,
      member_type_id: memberTypeId || undefined,
      accepted_term_ids: ids.length > 0 ? ids : undefined,
    };
  };

  const buildQueue = (): Method[] => {
    const preferred: Method =
      verificationMode === 'mobile'
        ? 'mobile'
        : verificationMode === 'email'
          ? 'email'
          : verifyMethod;
    return verificationMode === 'both'
      ? [preferred, preferred === 'email' ? 'mobile' : 'email']
      : [preferred];
  };

  // ── Signup mutations ──────────────────────────────────────────
  const startSignup = useMutation({
    mutationFn: async () => {
      const q = buildQueue();
      await requestSignupOtp({
        method: q[0]!,
        email: showEmail ? email.trim() : undefined,
        mobile: showMobile && mobileE164 ? mobileE164 : undefined,
        memberData: buildMemberData(),
      });
      return q;
    },
    onSuccess: (q) => {
      setQueue(q);
      setQIndex(0);
      setOtpCode('');
      setError(null);
      setPhase('otp');
    },
    onError: (e) => setError(toApiError(e).message),
  });

  const verifyOtp = useMutation({
    mutationFn: async () => {
      const method = queue[qIndex]!;
      const emailArg = showEmail && email.trim() ? email.trim() : undefined;
      const mobileArg = showMobile && mobileE164 ? mobileE164 : undefined;
      if (qIndex === queue.length - 1) {
        await verifySignupAndCreate({ method, email: emailArg, mobile: mobileArg, otpCode, password });
        const identifier =
          (method === 'mobile' ? mobileE164 : email.trim()) || email.trim() || mobileE164;
        await login({ identifier, password });
        return { done: true as const };
      }
      await verifySignupOtp({ method, email: emailArg, mobile: mobileArg, otpCode });
      const next = queue[qIndex + 1]!;
      await requestSignupOtp({
        method: next,
        email: emailArg,
        mobile: mobileArg,
        memberData: buildMemberData(),
      });
      return { done: false as const };
    },
    onSuccess: (res) => {
      if (res.done) {
        completedRef.current = true;
        navigate('/app/home', { replace: true });
      } else {
        setQIndex((i) => i + 1);
        setOtpCode('');
        setError(null);
      }
    },
    onError: (e) => setError(toApiError(e).message),
  });

  const resendOtp = useMutation({
    mutationFn: () =>
      requestSignupOtp({
        method: queue[qIndex]!,
        email: showEmail && email.trim() ? email.trim() : undefined,
        mobile: showMobile && mobileE164 ? mobileE164 : undefined,
        memberData: buildMemberData(),
      }),
    onError: (e) => setError(toApiError(e).message),
  });

  const validateForm = (): string | null => {
    if (memberTypes.length > 0 && !memberTypeId) return 'Please choose how you belong to the estate.';
    if (!firstName.trim() || !lastName.trim()) return 'Enter your first and last name.';
    if (emailRequired && !emailOk) return 'Enter a valid email address.';
    if (emailRequired && emailTaken) return 'That email is already registered. Try signing in.';
    if (mobileRequired && mobileE164.length < 8) return 'Enter a valid mobile number.';
    if (mobileRequired && mobileTaken) return 'That mobile number is already registered.';
    if (password.length < 8) return 'Your password must be at least 8 characters.';
    if (password !== confirm) return 'Passwords do not match.';
    if (!termsOk) return 'Please accept the terms to continue.';
    return null;
  };

  const onSubmitForm = () => {
    const message = validateForm();
    if (message) {
      setError(message);
      return;
    }
    setError(null);
    startSignup.mutate();
  };

  // "Change community" — go back to the welcome screen's community picker.
  // The unmount cleanup reverts branding + domain context to the hub.
  const changeCommunity = () => navigate('/welcome?signup=1');

  const otpMethod = queue[qIndex];
  const otpTarget = otpMethod === 'mobile' ? mobileE164 : email.trim();

  /* ── SSO build with no community picked — bounce to the picker ──── */
  if (rootTenantId && !tenantParam) {
    return <Navigate to="/welcome?signup=1" replace />;
  }

  /* ── OTP phase ────────────────────────────────────────────────── */
  if (phase === 'otp') {
    return (
      <div className="flex min-h-full flex-col bg-paper px-6 pt-safe-or-6 pb-safe-or-6">
        <button
          type="button"
          onClick={() => {
            setError(null);
            setPhase('form');
          }}
          aria-label="Back"
          className="flex h-9 w-9 items-center justify-center rounded-pill bg-sunken text-ink"
        >
          <ArrowLeft className="h-4.5 w-4.5" strokeWidth={1.75} />
        </button>

        <span className="mt-8 text-[0.6875rem] font-semibold uppercase tracking-[0.16em] text-brass">
          {queue.length > 1
            ? `Verification ${qIndex + 1} of ${queue.length}`
            : authCopy.signupOtpEyebrow}
        </span>
        <h1 className="display-serif mt-2 text-display-lg leading-tight text-ink">
          {authCopy.signupOtpTitle}
        </h1>
        <p className="mt-2 text-body text-ink-secondary">
          {interpolateCopy(authCopy.signupOtpSubtitle, { email: otpTarget })}
        </p>

        {error ? <Banner variant="danger" title={error} className="mt-4 rounded-md" /> : null}

        <form
          className="mt-6 flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            verifyOtp.mutate();
          }}
        >
          <Field
            label="One-time code"
            value={otpCode}
            onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="6-digit code"
          />
          <Button
            type="submit"
            size="lg"
            width="full"
            loading={verifyOtp.isPending}
            disabled={otpCode.length < 4}
          >
            {qIndex === queue.length - 1 ? 'Verify & create account' : 'Verify & continue'}
          </Button>
        </form>

        <button
          type="button"
          disabled={resendOtp.isPending}
          onClick={() => {
            setError(null);
            resendOtp.mutate();
          }}
          className="mt-5 text-center text-body-sm font-medium text-primary-700 disabled:opacity-50"
        >
          {resendOtp.isPending ? 'Sending…' : 'Resend code'}
        </button>
      </div>
    );
  }

  /* ── Form phase ───────────────────────────────────────────────── */
  return (
    <div className="flex min-h-full flex-col bg-paper px-6 pt-safe-or-6 pb-safe-or-6">
      <button
        type="button"
        onClick={() => navigate('/welcome')}
        aria-label="Back"
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-pill bg-sunken text-ink"
      >
        <ArrowLeft className="h-4.5 w-4.5" strokeWidth={1.75} />
      </button>

      <span className="mt-6 text-[0.6875rem] font-semibold uppercase tracking-[0.16em] text-brass">
        {authCopy.signupEyebrow}
      </span>
      <h1 className="display-serif mt-2 text-display-lg leading-tight text-ink">
        {authCopy.signupTitle}
      </h1>
      {rootTenantId ? (
        <button
          type="button"
          onClick={changeCommunity}
          className="mt-1.5 self-start text-body-sm font-medium text-primary-700"
        >
          Change community
        </button>
      ) : null}

      {error ? (
        <Banner
          variant="danger"
          title="Check your details"
          description={error}
          onDismiss={() => setError(null)}
          className="mt-4 rounded-md"
        />
      ) : null}

      <div className="mt-6 flex flex-col gap-4 pb-4" onKeyDown={focusNextOnEnter} onFocus={applyEnterKeyHint}>
        {/* Member type */}
        {memberTypesQuery.isLoading ? (
          <Skeleton className="h-16" />
        ) : memberTypes.length > 0 ? (
          <div>
            <span className="mb-1.5 block text-[0.6875rem] font-semibold uppercase tracking-[0.13em] text-brass">
              How do you belong here?
            </span>
            <div className="flex flex-col gap-2">
              {memberTypes.map((type) => {
                const on = memberTypeId === type.member_type_id;
                return (
                  <button
                    key={type.member_type_id}
                    type="button"
                    onClick={() => setMemberTypeId(type.member_type_id)}
                    className={
                      'flex items-center gap-3 rounded-lg border px-3.5 py-3 text-left transition-colors ' +
                      (on
                        ? 'border-primary-600 bg-primary-50'
                        : 'border-hairline-strong bg-elevated')
                    }
                  >
                    <span
                      className={
                        'flex h-5 w-5 shrink-0 items-center justify-center rounded-pill border ' +
                        (on
                          ? 'border-primary-600 bg-primary-600 text-white'
                          : 'border-hairline-strong')
                      }
                    >
                      {on ? <Check className="h-3 w-3" strokeWidth={3} /> : null}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-body text-ink">{type.name}</span>
                      {type.description ? (
                        <span className="block text-caption text-ink-muted">
                          {type.description}
                        </span>
                      ) : null}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}

        <div className="grid grid-cols-2 gap-3">
          <Field
            label="First name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            autoComplete="given-name"
            placeholder="Alex"
          />
          <Field
            label="Last name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            autoComplete="family-name"
            placeholder="Smith"
          />
        </div>

        {showEmail ? (
          <Field
            label={emailRequired ? 'Email' : 'Email (optional)'}
            type="email"
            inputMode="email"
            autoComplete="email"
            autoCapitalize="off"
            autoCorrect="off"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            error={
              email && !emailOk
                ? 'Enter a valid email'
                : emailTaken
                  ? 'Already registered'
                  : undefined
            }
            helper={
              emailAvail.isFetching && emailOk
                ? 'Checking availability…'
                : emailAvail.data?.email?.available
                  ? 'Email is available.'
                  : undefined
            }
          />
        ) : null}

        {showMobile ? (
          <div>
            <span className="mb-1.5 block text-[0.6875rem] font-semibold uppercase tracking-[0.13em] text-brass">
              {mobileRequired ? 'Mobile number' : 'Mobile number (optional)'}
            </span>
            <div className="flex gap-2">
              <CountryCodePicker
                value={countryCode}
                onChange={(c) => setCountryCode(c.code)}
              />
              <input
                value={localMobile}
                onChange={(e) => setLocalMobile(e.target.value.replace(/\D/g, '').slice(0, 15))}
                type="tel"
                inputMode="tel"
                autoComplete="tel-national"
                placeholder="82 123 4567"
                className="h-12 w-full rounded-md border border-hairline-strong bg-elevated px-3.5 text-body text-ink placeholder:text-ink-muted focus:border-primary-600 focus:outline-none"
              />
            </div>
            <p className="mt-1 text-caption text-ink-muted">
              {mobileTaken
                ? 'This number is already registered.'
                : mobileAvail.isFetching && debouncedMobile.length >= 8
                  ? 'Checking availability…'
                  : mobileAvail.data?.mobile?.available
                    ? 'Mobile number is available.'
                    : mobileE164
                      ? `Will register as ${mobileE164}`
                      : 'Used for SMS verification and alerts.'}
            </p>
          </div>
        ) : null}

        {showMethodToggle ? (
          <div>
            <span className="mb-1.5 block text-[0.6875rem] font-semibold uppercase tracking-[0.13em] text-brass">
              Verify with
            </span>
            <div className="flex gap-1 rounded-pill bg-sunken p-1">
              {([
                ['email', 'Email'],
                ['mobile', 'Mobile'],
              ] as const).map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setVerifyMethod(id)}
                  className={
                    'flex-1 rounded-pill py-2 text-body-sm font-medium transition-colors ' +
                    (verifyMethod === id ? 'bg-card text-ink shadow-card' : 'text-ink-secondary')
                  }
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        ) : null}

        <Field
          label="Password"
          type={showPassword ? 'text' : 'password'}
          autoComplete="new-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="At least 8 characters"
          trailing={
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              className="text-ink-muted"
            >
              {showPassword ? (
                <EyeOff className="h-4.5 w-4.5" strokeWidth={1.5} />
              ) : (
                <Eye className="h-4.5 w-4.5" strokeWidth={1.5} />
              )}
            </button>
          }
        />
        <Field
          label="Confirm password"
          type={showPassword ? 'text' : 'password'}
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          placeholder="Re-enter your password"
          error={confirm && confirm !== password ? 'Passwords do not match' : undefined}
        />

        {/* Terms — tenant documents, else the env fallback URL */}
        <TermsGate
          terms={terms}
          loading={termsQuery.isLoading}
          accepted={acceptedTermIds}
          onToggleTerm={(id) =>
            setAcceptedTermIds((p) => ({ ...p, [id]: !p[id] }))
          }
          agreed={agreed}
          onToggleAgreed={() => setAgreed((v) => !v)}
        />
      </div>

      <div className="mt-auto pt-4">
        <Button
          variant="primary"
          size="lg"
          width="full"
          loading={startSignup.isPending}
          onClick={onSubmitForm}
        >
          Create account
        </Button>
      </div>

      <p className="mt-4 text-center text-body-sm text-ink-secondary">
        Already registered?{' '}
        <button
          type="button"
          onClick={() => navigate('/login')}
          className="font-semibold text-primary-700"
        >
          Sign in
        </button>
      </p>
    </div>
  );
}

/* ── Terms gate ──────────────────────────────────────────────────────── */

interface TermDoc {
  terms_id: string;
  title: string;
  external_url?: string | null;
}

function TermsGate({
  terms,
  loading,
  accepted,
  onToggleTerm,
  agreed,
  onToggleAgreed,
}: {
  terms: TermDoc[];
  loading: boolean;
  accepted: Record<string, boolean>;
  onToggleTerm: (id: string) => void;
  agreed: boolean;
  onToggleAgreed: () => void;
}): JSX.Element {
  const box = (on: boolean) => (
    <span
      className={
        'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-xs border transition-colors ' +
        (on ? 'border-primary-600 bg-primary-600 text-white' : 'border-hairline-strong bg-elevated')
      }
    >
      {on ? <Check className="h-3.5 w-3.5" strokeWidth={3} /> : null}
    </span>
  );

  if (loading) return <Skeleton className="h-10" />;

  if (terms.length > 0) {
    return (
      <div className="flex flex-col gap-2.5">
        {terms.map((t) => {
          const on = !!accepted[t.terms_id];
          return (
            <div key={t.terms_id} className="flex items-start gap-2.5">
              <button
                type="button"
                onClick={() => onToggleTerm(t.terms_id)}
                aria-label={`Accept ${t.title}`}
              >
                {box(on)}
              </button>
              <p className="text-body-sm text-ink-secondary">
                I accept the{' '}
                {t.external_url ? (
                  <a
                    href={t.external_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold text-primary-700 underline"
                  >
                    {t.title}
                  </a>
                ) : (
                  <span className="font-semibold text-ink">{t.title}</span>
                )}
                .
              </p>
            </div>
          );
        })}
      </div>
    );
  }

  // No tenant terms configured — fall back to the env-configured URL.
  return (
    <div className="flex items-start gap-2.5">
      <button type="button" onClick={onToggleAgreed} aria-label="Accept the terms">
        {box(agreed)}
      </button>
      <p className="text-body-sm text-ink-secondary">
        I agree to the{' '}
        {env.signupTermsUrl ? (
          <a
            href={env.signupTermsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-primary-700 underline"
          >
            Terms &amp; Conditions
          </a>
        ) : (
          <span className="font-semibold text-ink">Terms &amp; Conditions</span>
        )}
        .
      </p>
    </div>
  );
}
